import { useCallback, useEffect, useRef, useState } from "react";

import { api, ApiError, readToken } from "./api";
import { Answers } from "./components/Answers";
import { Art } from "./components/Art";
import { Aside } from "./components/Aside";
import { Gate } from "./components/Gate";
import { LogoMarki } from "./components/LogoMarki";
import { MotywSwitch, useMotyw } from "./components/Motyw";
import { Outcome } from "./components/Outcome";
import { Finished } from "./components/Finished";
import { brandYbug, identifyYbug } from "./lib/ybug";
import { Start } from "./components/Start";
import { NoteCard } from "./components/fields";
import { AddressInput } from "./fields/AddressInput";
import { FileInput } from "./fields/FileInput";
import { KycWidget } from "./fields/KycWidget";
import { SignField } from "./fields/SignField";
import { CheckboxInput, SelectInput, TextInput } from "./fields/inputs";
import { LOCALES, t } from "./i18n";
import { koloryYbug, zastosujMarke } from "./lib/marka";
import { useInstallPrompt, useOnline } from "./lib/pwa";
import type { Branding, Locale, OnboardingState, RecordMeta, Stage } from "./types";

function recordIdFromUrl(): string | null {
  const match = window.location.pathname.match(/\/o\/([\w-]+)/);
  return match ? match[1] : null;
}

export default function App() {
  const recordId = recordIdFromUrl();
  const [meta, setMeta] = useState<RecordMeta | null>(null);
  const [state, setState] = useState<OnboardingState | null>(null);
  const [locale, setLocale] = useState<Locale>("pl");
  const [authorised, setAuthorised] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [fatal, setFatal] = useState(false);
  const [forceUpload, setForceUpload] = useState<string | null>(null);
  const [autofilled, setAutofilled] = useState<string[]>([]);
  const [helpSent, setHelpSent] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpComment, setHelpComment] = useState("");

  const online = useOnline();
  const install = useInstallPrompt();
  const activeRef = useRef<HTMLDivElement>(null);
  const seenKeys = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!recordId) return;
    api
      .meta(recordId)
      .then((loaded) => {
        // Skórka marki przed pierwszym ekranem procesu: do tej chwili kandydat
        // widzi tylko płótno i wskaźnik ładowania.
        zastosujMarke(recordId, loaded.branding);
        brandYbug(koloryYbug(loaded.branding));
        setMeta(loaded);
        setLocale(loaded.locale);
        setAuthorised(Boolean(readToken(recordId)));
      })
      .catch(() => setFatal(true));
  }, [recordId]);

  // Widżet zgłoszeń Ybug (gdy backend podał identyfikator projektu). Kandydat
  // dostaje wyłącznie wewnętrzne ID sprawy i język — bez imienia, telefonu i e-maila.
  useEffect(() => {
    if (!recordId) return;
    api.health()
      // Formularz zgłoszenia u kandydata bez danych osobowych — tylko ID sprawy i język.
      .then((health) => identifyYbug(health.ybug, { user: { id: recordId, language: locale }, language: locale }))
      .catch(() => undefined);
  }, [recordId, locale]);

  const refresh = useCallback(
    async (nextLocale?: Locale) => {
      if (!recordId) return;
      try {
        setState(await api.state(recordId, nextLocale ?? locale));
      } catch (caught) {
        const failure = caught as ApiError;
        if (failure.status === 401) setAuthorised(false);
        else setFieldError(failure.message);
      }
    },
    [recordId, locale],
  );

  useEffect(() => {
    if (authorised) void refresh();
  }, [authorised, refresh]);

  // Przewijamy do nowego pytania; wypełnione pola zostają na miejscu i się nie animują.
  useEffect(() => {
    const key = state?.current?.key;
    if (!key || seenKeys.current.has(key)) return;
    seenKeys.current.add(key);
    requestAnimationFrame(() =>
      activeRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }),
    );
  }, [state?.current?.key]);

  // Wstecz = popraw ostatnią rzecz, którą sam wpisałeś. Pól z prefillu i z KYC
  // nie proponujemy do cofania, bo nie użytkownik je wypełniał.
  const lastOwnAnswer = [...(state?.answered ?? [])].reverse().find((f) => f.origin === "user");

  const applyState = (next: OnboardingState) => {
    setState(next);
    setFieldError(null);
    setForceUpload(null);
    // KYC, OCR i geokodowanie zwracają listę pól, które wypełniły za użytkownika.
    setAutofilled(next.kyc?.filled ?? next.ocr?.filled ?? next.autofilled ?? []);
  };

  const submit = async (key: string, value: unknown) => {
    if (!recordId) return;
    setBusy(true);
    setFieldError(null);
    try {
      applyState(await api.answer(recordId, key, value, locale));
    } catch (caught) {
      setFieldError((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  const upload = async (key: string, file: File) => {
    if (!recordId) return;
    setBusy(true);
    setFieldError(null);
    try {
      const next = await api.upload(recordId, key, file, locale);
      if (next.ocr?.error === "nieczytelny_dokument") {
        setState(next);
        setFieldError(t("ocrUnreadable", locale));
      } else {
        applyState(next);
      }
    } catch (caught) {
      setFieldError((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  const finishFiles = async (key: string) => {
    if (!recordId) return;
    setBusy(true);
    setFieldError(null);
    try {
      applyState(await api.completeFiles(recordId, key, locale));
    } catch (caught) {
      setFieldError((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  const clear = async (key: string) => {
    if (!recordId) return;
    setBusy(true);
    try {
      applyState(await api.clear(recordId, key, locale));
    } catch (caught) {
      setFieldError((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  const askForHelp = async () => {
    if (!recordId) return;
    setBusy(true);
    try {
      await api.help(recordId, helpComment);
      setHelpSent(true);
      setHelpOpen(false);
    } catch (caught) {
      setFieldError((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  const switchLocale = async (next: Locale) => {
    setLocale(next);
    // Wybór zapamiętuje serwer, nie tylko ta karta. Wszystko, co dzieje się
    // bez udziału kandydata — ekran dostawcy KYC, karta w panelu, dokumenty
    // generowane po stronie konsultanta — inaczej zostawało po polsku.
    if (recordId) {
      void api.setLocale(recordId, next).catch(() => undefined);
      // Powitanie i finał marki też są tekstem z backendu — i trzeba ich już
      // na bramce, zanim kandydat ma sesję.
      void api.meta(recordId, next).then(setMeta).catch(() => undefined);
    }
    await refresh(next); // teksty pytań przychodzą z backendu, nie z bundla
  };

  // Goły adres to nie błąd — proces po prostu jeszcze się nie zaczął.
  if (!recordId) return <Shell locale={locale} onLocale={switchLocale}><Start locale={locale} /></Shell>;

  if (fatal) {
    // Zepsuty link to pierwszy ekran, jaki ten człowiek u nas widzi — dostaje
    // pełną oprawę (ilustracja, tytułowa waga tekstu), nie goły komunikat.
    return (
      <Shell locale={locale} onLocale={switchLocale}>
        <div className="center">
          <Art name="start-link" size={132} className="center__art" />
          <p className="center__title">{t("linkBroken", locale)}</p>
          <a className="btn btn--secondary" href="/">
            {t("startOpen", locale)}
          </a>
        </div>
      </Shell>
    );
  }

  if (!meta) {
    return (
      <Shell locale={locale} onLocale={switchLocale}>
        <div className="center">
          <div className="spinner" />
          <p>{t("loading", locale)}</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell
      locale={locale}
      onLocale={switchLocale}
      progress={state?.progress ?? 0}
      stages={state?.stages ?? []}
      online={online}
      install={install}
      branding={meta.branding}
    >
      {!authorised ? (
        <Gate meta={meta} locale={locale} onVerified={() => setAuthorised(true)} />
      ) : !state ? (
        <div className="center">
          <div className="spinner" />
        </div>
      ) : (
        <div className="flow">
          {/* Finał dostaje cały ekran — kaskada odpowiedzi tylko by go zagłuszyła.
              Kolejność jest istotna: odmowa konsultanta bije nawet notę końcową
              (kandydat doszedł do „gotowe", ale został odrzucony — ma zobaczyć
              odmowę, nie sukces). Sukces natomiast zostawia notę końcową ze
              schematu, jeśli kandydat do niej doszedł: `koniec_*` ma dopasowaną
              treść i podpowiada dalsze kroki. Generyczny ekran sukcesu wchodzi
              tylko, gdy sprawę domknięto sukcesem bez dojścia do noty końcowej. */}
          {state.outcome === "failed" ? (
            <Finished
              outcome="failed"
              locale={locale}
              recordId={state.record_id}
              fail={meta.branding?.texts.fail}
              bezInfoliniiCityDrive={!!meta.branding && meta.branding.key !== "classic"}
            />
          ) : state.current?.severity === "final" ? (
            <Outcome
              datapoint={state.current}
              locale={locale}
              documents={state.answered.filter((f) => f.type === "simple_sign")}
              recordId={state.record_id}
              busy={busy}
              onBack={lastOwnAnswer ? () => clear(lastOwnAnswer.key) : undefined}
              final={meta.branding?.texts.final}
            />
          ) : state.outcome === "success" ? (
            <Finished
              outcome="success"
              locale={locale}
              documents={state.answered.filter((f) => f.type === "simple_sign")}
              recordId={state.record_id}
              final={meta.branding?.texts.final}
            />
          ) : (
            <>
              <Answers
                fields={state.answered}
                locale={locale}
                busy={busy}
                autofilled={autofilled}
                onClear={clear}
                recordId={state.record_id}
              />

              {state.current && (
                // `field--active` niesie kartę (jedyny cień na ekranie),
                // `field--enter` samo wejście — animacja osobno od układu.
                <div className="field field--active field--enter" ref={activeRef}>
                  {state.current.type !== "note" && (
                    <>
                      <h2 className="question">{state.current.title}</h2>
                      {state.current.subtitle && (
                        <p className="field__sub">{state.current.subtitle}</p>
                      )}
                    </>
                  )}
                  {renderActive()}

                  <div className="nav">
                    {lastOwnAnswer && (
                      <button
                        className="nav__back"
                        disabled={busy}
                        onClick={() => clear(lastOwnAnswer.key)}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                          <path
                            d="M14 6l-6 6 6 6"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        {t("back", locale)}
                      </button>
                    )}

                    {/* Deklaracja, nie wniosek z timera: zegar nie odróżni
                        człowieka, który utknął, od tego, który wyszedł na obiad. */}
                    <span className="nav__spacer" />
                    {helpSent ? (
                      <span className="nav__helpdone" role="status">{t("helpSent", locale)}</span>
                    ) : (
                      <button
                        className="nav__help"
                        disabled={busy}
                        aria-expanded={helpOpen}
                        onClick={() => setHelpOpen((otwarte) => !otwarte)}
                      >
                        {t("helpButton", locale)}
                      </button>
                    )}
                  </div>
                  {helpOpen && !helpSent && (
                    // Wolny tekst bywa danymi osobowymi — poza zrzuty Ybug.
                    <form
                      className="pomoc"
                      data-ybug-sensitive
                      onSubmit={(zdarzenie) => {
                        zdarzenie.preventDefault();
                        void askForHelp();
                      }}
                    >
                      <label className="field__sub" htmlFor="pomoc-komentarz">
                        {t("helpCommentLabel", locale)}
                      </label>
                      <textarea
                        id="pomoc-komentarz"
                        className="input"
                        rows={3}
                        maxLength={500}
                        value={helpComment}
                        onChange={(zdarzenie) => setHelpComment(zdarzenie.target.value)}
                      />
                      <div className="pomoc__akcje">
                        <button type="button" className="btn btn--secondary" onClick={() => setHelpOpen(false)}>
                          {t("helpCancel", locale)}
                        </button>
                        <button type="submit" className="btn btn--primary" disabled={busy}>
                          {t("helpSend", locale)}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Shell>
  );

  function renderActive() {
    const current = state?.current;
    if (!current || !recordId) return null;

    const common = {
      datapoint: current,
      locale,
      busy,
      error: fieldError,
      onSubmit: (value: unknown) => submit(current.key, value),
    };

    // Awaria dostawcy KYC przełącza krok na ręczne wgranie zdjęć.
    if (current.type === "idenfy" && forceUpload === current.key) {
      return (
        <FileInput
          datapoint={current}
          locale={locale}
          busy={busy}
          error={fieldError}
          onUpload={(file) => upload(current.key, file)}
        />
      );
    }

    switch (current.type) {
      case "note":
        return <NoteCard datapoint={current} />;
      case "select":
        return <SelectInput {...common} />;
      case "checkbox":
        return <CheckboxInput {...common} />;
      case "file":
        return (
          <FileInput
            datapoint={current}
            locale={locale}
            busy={busy}
            error={fieldError}
            onUpload={(file) => upload(current.key, file)}
            files={state?.files}
            onFinish={() => finishFiles(current.key)}
          />
        );
      case "idenfy":
        return (
          <KycWidget
            recordId={recordId}
            datapoint={current}
            locale={locale}
            onDone={applyState}
            onFallback={(message) => {
              setForceUpload(current.key);
              setFieldError(message);
            }}
          />
        );
      case "address":
        return (
          <AddressInput
            recordId={recordId}
            datapoint={current}
            locale={locale}
            busy={busy}
            onSelected={applyState}
            onManual={(value) => submit(current.key, value)}
          />
        );
      case "simple_sign":
        return (
          <SignField recordId={recordId} datapoint={current} locale={locale} onSigned={applyState} />
        );
      default:
        return <TextInput {...common} />;
    }
  }
}

/** Wspólny szkielet: kolorowa kolumna z kontekstem + biała kolumna z formularzem. */
/** Tryb instancji pytamy raz — w trakcie sesji i tak się nie zmieni. */
let demoModeCache: boolean | null = null;

function useDemoMode(): boolean {
  const [demo, setDemo] = useState(demoModeCache ?? false);

  useEffect(() => {
    if (demoModeCache !== null) return;
    api
      .health()
      .then((health) => {
        demoModeCache = Boolean(health.dev_mode);
        setDemo(demoModeCache);
      })
      // Brak odpowiedzi traktujemy jak produkcję: fałszywe „to tylko demo"
      // jest groźniejsze niż brak plakietki.
      .catch(() => {
        demoModeCache = false;
      });
  }, []);

  return demo;
}

function Shell({
  children,
  locale,
  onLocale,
  progress = 0,
  stages = [],
  online = true,
  install,
  branding,
}: {
  children: React.ReactNode;
  locale: Locale;
  onLocale: (next: Locale) => void;
  progress?: number;
  stages?: Stage[];
  online?: boolean;
  install?: { available: boolean; prompt: () => void; dismiss: () => void };
  /** Szablon marki sprawy; bez niego (ekran startowy, ładowanie) — for apps. */
  branding?: Branding;
}) {
  const demo = useDemoMode();
  const [motyw, setMotyw] = useMotyw();
  const currentStep = Math.max(1, stages.findIndex((stage) => stage.state === "current") + 1);

  return (
    <div className="shell">
      <Aside stages={stages} progress={progress} locale={locale} motyw={motyw} branding={branding} />

      <div className="pane">
        <header className="topbar">
          <div className="topbar__row">
            <LogoMarki branding={branding} motyw={motyw} miejsce="pasek" />
            <span className="topbar__spacer" />
            <span className="pct" aria-live="polite">
              {/* Klucz z wartości: nowy procent montuje świeży element i wjeżdża
                  jak licznik; sam .pct stoi w miejscu (kontrakt testów). */}
              <span key={progress} className="pct__num wjazd">
                {progress}%
              </span>
            </span>
            {/* Język i motyw stoją razem: jedno i drugie to ustawienie widoku,
                a nie krok procesu. Wybór języka zostaje natywnym <select> —
                na telefonie otwiera systemową listę, która radzi sobie z czterema
                alfabetami lepiej niż cokolwiek własnego. */}
            <div className="topbar__tools">
              <MotywSwitch
                motyw={motyw}
                onMotyw={setMotyw}
                etykiety={{ jasny: t("motywJasny", locale), ciemny: t("motywCiemny", locale) }}
              />
              <select
                className="select"
                aria-label={t("jezyk", locale)}
                value={locale}
                onChange={(event) => onLocale(event.target.value as Locale)}
              >
                {LOCALES.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {stages.length > 0 && (
            <>
              <div className="steps" aria-hidden="true">
                {stages.map((stage) => (
                  <span
                    key={stage.id}
                    className={`steps__seg${
                      stage.state === "done"
                        ? " steps__seg--done"
                        : stage.state === "current"
                          ? " steps__seg--current"
                          : ""
                    }`}
                  />
                ))}
              </div>
              <span className="steps__label" aria-live="polite">
                <Art
                  name={stages.find((stage) => stage.state === "current")?.id ?? "start"}
                  size={30}
                  className="steps__art"
                />
                {t("stepOf", locale)} {currentStep} {t("stepOfMid", locale)} {stages.length} ·{" "}
                <b>{stages.find((stage) => stage.state === "current")?.label ?? ""}</b>
              </span>
            </>
          )}
        </header>

        <main className="content">{children}</main>
      </div>

      {/* Wspólny kosz na banery: demo i instalacja PWA potrafią wystąpić
          naraz, a dwa elementy `position: fixed` w tym samym rogu nakrywały
          się nawzajem — ostrzeżenie o wersji demonstracyjnej znikało pod
          podpowiedzią instalacji. */}
      <div className="banners">
        {demo && (
          <div className="banner banner--demo" role="status">
            <span className="banner__text">{t("demoBanner", locale)}</span>
          </div>
        )}

        {!online && (
          <div className="banner banner--warn" role="status">
            <span className="banner__text">{t("offline", locale)}</span>
          </div>
        )}

        {online && install?.available && (
          <div className="banner">
            <span className="banner__text">{t("installHint", locale)}</span>
            <button className="banner__btn" onClick={install.prompt}>
              {t("install", locale)}
            </button>
            <button className="banner__btn" onClick={install.dismiss}>
              {t("dismiss", locale)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
